import { CrossBlackIcon } from "@/src/Asset";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

const redirectToNewUrl = async (
  router: any,
  colleges: any,
  location: any,
  filterOptions: any,
  setIsRedirecting: any,
  removedOption?: string
) => {
  if (!colleges || colleges.length === 0 || !location) return;

  setIsRedirecting(true);

  const newLocation = getNewLocation(filterOptions);
  if (!newLocation) {
    setIsRedirecting(false);
    return;
  }

  const finalUpdateValue = getFinalUpdateValue(filterOptions, newLocation, removedOption);
  const updatedColleges = [...colleges];
  updatedColleges[updatedColleges.length - 1] = finalUpdateValue;

  await router.push({
    pathname: router.pathname,
    query: { ...router.query, colleges: updatedColleges },
  });

  setIsRedirecting(false);
};

const getNewLocation = (filterOptions: any) => {
  return (
    filterOptions.city?.city_url ||
    filterOptions.state?.state_url ||
    filterOptions.country?.country_url?.replace("study-in-", "")
  );
};

const getFinalUpdateValue = (filterOptions: any, newLocation: string, removedOption?: string) => {
  if (removedOption === "specialization") {
    return handleSpecializationRemoval(filterOptions, newLocation);
  }
  return handleStandardPriority(filterOptions, newLocation);
};

const handleSpecializationRemoval = (filterOptions: any, newLocation: string) => {
  let previousCourseUrl;
  try {
    previousCourseUrl = localStorage.getItem("previousCourseUrl");
  } catch (e) {
    console.warn("Failed to get previous course URL from localStorage:", e);
  }

  const courseUrl = filterOptions?.course?.course_url ?? previousCourseUrl;
  if (courseUrl) {
    return `${courseUrl}-colleges-in-${newLocation}`;
  } else if (filterOptions?.stream?.stream_name) {
    return `${filterOptions.stream.stream_name
      .toLowerCase()
      .replace(/\s+/g, "-")}-colleges-in-${newLocation}`;
  }
  return `colleges-in-${newLocation}`;
};

const handleStandardPriority = (filterOptions: any, newLocation: string) => {
  if (filterOptions?.specialization?.specialization_url) {
    if (filterOptions?.course?.course_url) {
      return `${filterOptions.course.course_url}/${filterOptions.specialization.specialization_url}-colleges-in-${newLocation}`;
    }
    return `${filterOptions.specialization.specialization_url}-colleges-in-${newLocation}`;
  } else if (filterOptions?.course?.course_url) {
    return `${filterOptions.course.course_url}-colleges-in-${newLocation}`;
  } else if (filterOptions?.stream?.stream_name) {
    return `${filterOptions.stream.stream_name
      .toLowerCase()
      .replace(/\s+/g, "-")}-colleges-in-${newLocation}`;
  }
  return `colleges-in-${newLocation}`;
};

interface SelectedOptionsSectionProps {
  readonly filterOptions: any;
  readonly collegesUrl: any;
  readonly location: any;
  readonly setSelectedFilterCategory: (category: string | null) => void;
}

export default function SelectedOptionsSection({
  filterOptions,
  collegesUrl,
  location,
  setSelectedFilterCategory,
}: SelectedOptionsSectionProps) {
  const router = useRouter();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [newFilterOption, setNewFilterOption] = useState(filterOptions);

  const [selectedOptions, setSelectedOptions] = useState(
    [
      ...(filterOptions?.country?.country_name &&
      filterOptions.country.country_name !== "India"
        ? [
            {
              key: "country",
              name:
                filterOptions.country.country_name === "Abroad"
                  ? "All Countries"
                  : filterOptions.country.country_name,
            },
          ]
        : []),
      { key: "state", name: filterOptions?.state?.state_name },
      { key: "city", name: filterOptions?.city?.city_name },
      { key: "stream", name: filterOptions?.stream?.stream_name },
      { key: "course", name: filterOptions?.course?.course_name },
      {
        key: "specialization",
        name: filterOptions?.specialization?.specialization_name,
      },
    ].filter((option) => option.name)
  );

  useEffect(() => {
    setNewFilterOption(filterOptions);
    
    // Create a Set to track unique names
    const uniqueNames = new Set();
    
    const data = [
      ...(filterOptions?.country?.country_name &&
      filterOptions.country.country_name !== "India"
        ? [
            {
              key: "country",
              name:
                filterOptions.country.country_name === "Abroad"
                  ? "All Countries"
                  : filterOptions.country.country_name,
            },
          ]
        : []),
      { key: "state", name: filterOptions?.state?.state_name },
      { key: "city", name: filterOptions?.city?.city_name },
      ...(filterOptions?.stream
        ? [{
            key: "stream",
            name: filterOptions?.stream?.stream_name
          }]
        : []),
      ...(filterOptions?.course
        ? [{
            key: "course",
            name: filterOptions?.course?.course_name,
            parentKey: "stream"
          }]
        : []),
      ...(filterOptions?.specialization
        ? [{
            key: "specialization",
            name: filterOptions?.specialization?.specialization_name,
            parentKey: "course"
          }]
        : []),
    ]
    // Filter out options with no name and remove duplicates
    .filter((option) => {
      if (!option.name) return false;
      if (uniqueNames.has(option.name)) return false;
      uniqueNames.add(option.name);
      return true;
    });

    setSelectedOptions(data);
  }, [filterOptions]);

  const handleRemoveOption = (key: string, name: string) => {
    let updated = { ...newFilterOption };

    switch (key) {
      case "country":
        if (name !== "All Countries") {
          updated = {
            ...updated,
            country: {
              id: null,
              country_name: "Abroad",
              country_url: "study-in-abroad",
            },
            state: null,
            city: null,
          };
        }
        break;
      case "state":
        updated.state = null;
        updated.city = null;
        break;
      case "city":
        updated.city = null;
        break;
      case "stream":
        updated.stream = null;
        updated.course = null;
        updated.specialization = null;
        break;
      case "course":
        updated.course = null;
        updated.specialization = null;
        break;
      case "specialization":
        updated.specialization = null;
        break;
      default:
        break;
    }

    return updated;
  };

  const handleRemoveAndRedirect = async (key: string, name: string) => {
    if (isRedirecting) return;

    const updatedFilters = handleRemoveOption(key, name);
    setNewFilterOption(updatedFilters);

    try {
      if (key === "specialization") {
        setIsRedirecting(true);
        // Use browser history to go back to previous URL
        router.back();
        setIsRedirecting(false);
      } else if (key === "course") {
        setIsRedirecting(true);
        const streamName = filterOptions?.stream?.stream_name?.toLowerCase().replace(/\s+/g, "-");
        const locInfo = filterOptions.city?.city_url ??
                       filterOptions.state?.state_url ?? 
                       filterOptions.country?.country_url?.replace("study-in-", "") ??
                       "india";
        
        if (streamName) {
          await router.push(`/${streamName}-colleges-in-${locInfo}`);
        } else {
          await router.push(`/colleges-in-${locInfo}`);
        }
        setSelectedFilterCategory(null);
        setIsRedirecting(false);
      } else if (key === "stream") {
        setIsRedirecting(true);
        // Clear course and specialization from localStorage when stream changes
        try {
          localStorage.removeItem('previousCourseUrl');
          localStorage.removeItem('previousStreamName');
          localStorage.removeItem('previousStreamUrl');
        } catch (e) {
          console.warn('Failed to clear localStorage:', e);
        }

        const locInfo = filterOptions.city?.city_url ??
                       filterOptions.state?.state_url ?? 
                       filterOptions.country?.country_url?.replace("study-in-", "") ??
                       "india";
        
        await router.push(`/colleges-in-${locInfo}`);
        setSelectedFilterCategory(null);
        setIsRedirecting(false);
      } else {
        await redirectToNewUrl(
          router,
          collegesUrl,
          location,
          updatedFilters,
          setIsRedirecting,
          key
        );
      }
    } catch (error) {
      console.error("Redirect error:", error);
    } finally {
      setIsRedirecting(false);
    }
  };

  if (selectedOptions.length === 0) return null;
  return (
    <div className="flex lg:flex-wrap lg:items-center lg:border-t lg:border-gray-200 lg:mt-2 lg:pt-2.5 font-inter gap-x-2 gap-y-1">
      {selectedOptions.map((option: any) => (
        <div
          key={option.key}
          className={`flex items-center justify-between py-0.5 rounded-full text-xs font-medium bg-white border border-primary/50 text-primary ${
            option.parentKey ? 'ml-2' : ''
          }`}
        >
          <p className={`${option.name === "All Countries" ? "px-3" : "pl-3"} truncate`}>
            {option.name}
            {option.isParent && (
              <span className="text-gray-500 mx-1">→</span>
            )}
          </p>
          {!(option.name === "All Countries" || option.name === "India") && (
            <button
              disabled={isRedirecting}
              onClick={() => handleRemoveAndRedirect(option.key, option.name)}
              className={`mx-1 ${
                isRedirecting ? "bg-gray-300 cursor-not-allowed" : ""
              }`}
            >
              <CrossBlackIcon className="scale-[0.60] !text-primary" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}